// Time Furniture CRM — Sales Operations & Conversion Engine dashboard.
import React, { useCallback, useEffect, useState } from "react";
import { Calendar, TrendingUp, Target, CheckCircle2, XCircle, RefreshCw, UserPlus, FileText } from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import { PageHead } from "./crmUi";
import { useNavigate } from "react-router-dom";

const inputCls = "border border-[#6E1F2C]/20 bg-[#FAF5EC] px-2.5 py-1.5 text-[12px] text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";
const aed = (n) => (n || n === 0 ? `AED ${Number(n).toLocaleString()}` : "—");
const STAGE_LABEL = {
  new: "New", ai_qualifying: "AI Qualifying", qualified: "Qualified", human_takeover: "Human Takeover",
  site_visit_proposed: "Visit Proposed", site_visit_scheduled: "Visit Scheduled",
  measurement_completed: "Measured", quotation_pending: "Quote Pending", quotation_sent: "Quote Sent",
  negotiation: "Negotiation", follow_up: "Follow-up", won: "Won", lost: "Lost",
};
const APPT_COLORS = {
  confirmed: "text-[#3F6E2C] border-[#3F6E2C]/40", after_hours_approval: "text-[#A3141A] border-[#A3141A]/40",
  proposed: "text-[#A6822F] border-[#A6822F]/40", awaiting_confirmation: "text-[#A6822F] border-[#A6822F]/40",
  completed: "text-[#2C5F6E] border-[#2C5F6E]/40", cancelled: "text-[#7A6B5F] border-[#7A6B5F]/40",
  declined: "text-[#7A6B5F] border-[#7A6B5F]/40",
};

function PipelineTab() {
  const [p, setP] = useState(null);
  const [a, setA] = useState(null);
  useEffect(() => {
    crmApi.salesPipeline().then(setP).catch(() => {});
    crmApi.salesAnalytics().then(setA).catch(() => {});
  }, []);
  if (!p || !a) return <p className="text-xs text-[#7A6B5F]">Loading pipeline…</p>;
  const open = p.stages.filter((s) => !["won", "lost"].includes(s.stage) && s.count > 0);
  const f = a.funnel;
  const funnel = [["Enquiries", f.enquiries], ["Qualified", f.qualified], ["Site Visit+", f.site_visit], ["Quotation+", f.quotation], ["Won", f.won]];
  return (
    <div className="space-y-5" data-testid="sales-pipeline">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[["Open pipeline", `${aed(p.open_pipeline_value_min)} – ${aed(p.open_pipeline_value_max)}`, TrendingUp],
          ["Won value (est.)", aed(p.won_value_max), CheckCircle2],
          ["Win rate", a.win_rate != null ? `${a.win_rate}%` : "—", Target],
          ["Upcoming visits", a.upcoming_appointments, Calendar]].map(([t, v, I]) => (
          <div key={t} className="border border-[#6E1F2C]/15 bg-white p-3">
            <p className="text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F] flex items-center gap-1"><I size={11} /> {t}</p>
            <p className="text-[15px] text-[#1C110C] mt-1" data-testid={`sales-kpi-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</p>
          </div>
        ))}
      </div>
      <div className="border border-[#6E1F2C]/15 bg-white p-4">
        <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-2">Revenue pipeline by stage (AI-estimated, staff can override per lead)</p>
        {open.map((s) => (
          <div key={s.stage} className="flex items-center gap-2 text-[11px] py-1 border-b border-[#6E1F2C]/8" data-testid={`stage-${s.stage}`}>
            <span className="w-32 text-[#1C110C]">{STAGE_LABEL[s.stage] || s.stage}</span>
            <span className="w-8 text-[#7A6B5F]">{s.count}</span>
            <div className="flex-1 h-2 bg-[#6E1F2C]/8"><div className="h-2 bg-[#6E1F2C]" style={{ width: `${Math.min(100, (s.value_max / Math.max(p.open_pipeline_value_max, 1)) * 100)}%` }} /></div>
            <span className="w-44 text-right text-[#7A6B5F]">{aed(s.value_min)} – {aed(s.value_max)}</span>
          </div>
        ))}
        {open.length === 0 && <p className="text-[11px] text-[#7A6B5F] italic">No open leads with values yet.</p>}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-[#6E1F2C]/15 bg-white p-4">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-2">Conversion funnel — last {a.period_days} days</p>
          {funnel.map(([t, v], i) => (
            <div key={t} className="flex items-center gap-2 text-[11px] py-1">
              <span className="w-24 text-[#1C110C]">{t}</span>
              <div className="flex-1 h-2 bg-[#2C5F6E]/10"><div className="h-2 bg-[#2C5F6E]" style={{ width: `${f.enquiries ? (v / f.enquiries) * 100 : 0}%` }} /></div>
              <span className="w-10 text-right text-[#7A6B5F]">{v}</span>
            </div>
          ))}
          <p className="text-[10px] text-[#7A6B5F] mt-1 flex items-center gap-1"><XCircle size={10} /> Lost: {f.lost}</p>
        </div>
        <div className="border border-[#6E1F2C]/15 bg-white p-4" data-testid="sales-team">
          <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F] mb-2">Team performance</p>
          {a.team.map((t) => (
            <div key={t.name} className="flex justify-between text-[11px] py-1 border-b border-[#6E1F2C]/8">
              <span className="text-[#1C110C]">{t.name}</span>
              <span className="text-[#7A6B5F]">{t.leads} leads · {t.won} won · open {aed(t.open_value_max)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PrioritiesTab() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => crmApi.salesPriorities().then((d) => setRows(d.leads)).catch((e) => setMsg(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const assign = async (id) => { try { await crmApi.salesAssign(id); setMsg("Assigned ✓"); load(); } catch (e) { setMsg(e.message); } };
  const brief = async (id) => {
    setMsg("Generating quote brief…");
    try { await crmApi.salesQuoteBrief(id); setMsg("Quote brief ready — see the lead page ✓"); } catch (e) { setMsg(e.message); }
  };
  return (
    <div className="border border-[#6E1F2C]/15 bg-white" data-testid="sales-priorities">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#6E1F2C]/10">
        <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F]">Priority queue — work top-down</p>
        <div className="flex items-center gap-2">{msg && <span className="text-[10px] text-[#A6822F]">{msg}</span>}
          <button type="button" onClick={load} className="text-[#7A6B5F] hover:text-[#1C110C]"><RefreshCw size={12} /></button></div>
      </div>
      {rows.map((l) => (
        <div key={l.id} className="flex items-center gap-3 px-4 py-2 border-b border-[#6E1F2C]/8 text-[11px] hover:bg-[#FAF5EC]" data-testid="priority-row">
          <span className={`w-9 text-center font-semibold py-0.5 ${l.priority_score >= 60 ? "bg-[#A3141A] text-white" : l.priority_score >= 35 ? "bg-[#A6822F] text-white" : "bg-[#7A6B5F]/20 text-[#1C110C]"}`}>{l.priority_score}</span>
          <button type="button" className="text-[#6E1F2C] hover:underline w-16 text-left" onClick={() => nav(`/admin/crm/leads/${l.id}`)}>{l.lead_ref}</button>
          <span className="w-32 truncate text-[#1C110C]">{l.customer_name || "Unknown"}</span>
          <span className="w-32 truncate text-[#7A6B5F]">{(l.service_category || "—").replace(/_/g, " ")}</span>
          <span className="w-28 text-[#7A6B5F]">{STAGE_LABEL[l.lead_status] || l.lead_status}</span>
          <span className="w-36 text-[#7A6B5F]">{l.estimated_value_min ? `${aed(l.estimated_value_min)}–${aed(l.estimated_value_max)}` : "—"}</span>
          <span className="flex-1 truncate text-[#7A6B5F]">{l.assigned_salesperson_name || <em>unassigned</em>}</span>
          <div className="flex gap-1 shrink-0">
            {!l.assigned_salesperson_name && (
              <button type="button" title="Auto-assign" onClick={() => assign(l.id)} data-testid="priority-assign-btn"
                className="border border-[#6E1F2C]/30 text-[#6E1F2C] p-1 hover:bg-[#6E1F2C]/10"><UserPlus size={11} /></button>)}
            <button type="button" title="Quote brief" onClick={() => brief(l.id)} data-testid="priority-brief-btn"
              className="border border-[#A6822F]/40 text-[#A6822F] p-1 hover:bg-[#A6822F]/10"><FileText size={11} /></button>
          </div>
        </div>
      ))}
      {rows.length === 0 && <p className="text-xs text-[#7A6B5F] italic p-4">No open leads.</p>}
    </div>
  );
}

function AppointmentsTab() {
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ lead_id: "", start: "", appt_type: "site_visit" });
  const [msg, setMsg] = useState("");
  const load = useCallback(() => crmApi.salesAppointments().then((d) => setItems(d.appointments)).catch((e) => setMsg(e.message)), []);
  useEffect(() => { load(); crmApi.salesSlots().then((d) => setSlots(d.slots)).catch(() => {}); }, [load]);
  const act = async (fn, ok) => { try { await fn(); setMsg(ok); load(); } catch (e) { setMsg(e.message); } };
  const create = () => act(() => crmApi.salesCreateAppt(form), "Appointment created ✓");
  return (
    <div className="space-y-4" data-testid="sales-appointments">
      <div className="border border-[#6E1F2C]/15 bg-white p-4 space-y-2">
        <p className="text-[9px] tracking-[0.2em] uppercase text-[#A6822F]">Book appointment (Mon–Sat 09:00–18:00 · Sunday/evening → admin approval)</p>
        <div className="flex flex-wrap gap-2 items-center">
          <input className={inputCls} placeholder="Lead ID or ref" value={form.lead_id} onChange={(e) => setForm({ ...form, lead_id: e.target.value })} data-testid="appt-lead-input" />
          <input className={inputCls} type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="appt-start-input" />
          <select className={inputCls} value={form.appt_type} onChange={(e) => setForm({ ...form, appt_type: e.target.value })}>
            <option value="site_visit">Site visit</option><option value="virtual_call">Virtual (video)</option>
            <option value="phone">Phone call</option><option value="google_meet">Google Meet</option>
          </select>
          <button type="button" onClick={create} disabled={!form.lead_id || !form.start} data-testid="appt-create-btn"
            className="bg-[#6E1F2C] text-white px-3 py-1.5 text-[9px] uppercase tracking-[0.14em] disabled:opacity-50">Book</button>
          {msg && <span className="text-[10px] text-[#A6822F]" data-testid="appt-msg">{msg}</span>}
        </div>
        {slots.length > 0 && <p className="text-[10px] text-[#7A6B5F]">Next free slots: {slots.map((s) => new Date(s).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })).join(" · ")}</p>}
      </div>
      <div className="border border-[#6E1F2C]/15 bg-white">
        {items.map((a) => (
          <div key={a.id} className="flex items-center gap-3 px-4 py-2 border-b border-[#6E1F2C]/8 text-[11px]" data-testid="appt-row">
            <span className={`border px-1.5 py-0.5 text-[8px] uppercase ${APPT_COLORS[a.status] || ""}`} data-testid="appt-status">{a.status.replace(/_/g, " ")}</span>
            <span className="w-40 text-[#1C110C]">{new Date(a.start).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="w-20 text-[#6E1F2C]">{a.lead_ref}</span>
            <span className="w-32 truncate">{a.customer_name || "—"}</span>
            <span className="w-24 text-[#7A6B5F]">{(a.appt_type || "").replace(/_/g, " ")}</span>
            <span className="flex-1 truncate text-[#7A6B5F]">{a.salesperson_name || "unassigned"} · {a.location || ""}</span>
            <div className="flex gap-1 shrink-0">
              {a.status === "after_hours_approval" && (<>
                <button type="button" data-testid="appt-approve-btn" onClick={() => act(() => crmApi.salesPatchAppt(a.id, { status: "confirmed" }), "Approved ✓")}
                  className="border border-[#3F6E2C]/40 text-[#3F6E2C] px-1.5 py-0.5 text-[8px] uppercase hover:bg-[#3F6E2C]/10">Approve</button>
                <button type="button" onClick={() => act(() => crmApi.salesPatchAppt(a.id, { status: "declined" }), "Declined")}
                  className="border border-[#A3141A]/40 text-[#A3141A] px-1.5 py-0.5 text-[8px] uppercase hover:bg-[#A3141A]/10">Decline</button></>)}
              {["confirmed", "awaiting_confirmation", "proposed"].includes(a.status) && (<>
                <button type="button" data-testid="appt-complete-btn" onClick={() => act(() => crmApi.salesPatchAppt(a.id, { status: "completed" }), "Completed — quotation task created ✓")}
                  className="border border-[#2C5F6E]/40 text-[#2C5F6E] px-1.5 py-0.5 text-[8px] uppercase hover:bg-[#2C5F6E]/10">Done</button>
                <button type="button" onClick={() => act(() => crmApi.salesPatchAppt(a.id, { status: "cancelled" }), "Cancelled")}
                  className="border border-[#7A6B5F]/40 text-[#7A6B5F] px-1.5 py-0.5 text-[8px] uppercase hover:bg-[#7A6B5F]/10">Cancel</button></>)}
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-[#7A6B5F] italic p-4">No appointments yet — the AI books them from chat, or create one above.</p>}
      </div>
    </div>
  );
}

export default function SalesOpsPage() {
  const [tab, setTab] = useState("pipeline");
  const TABS = [["pipeline", "Pipeline & Analytics"], ["priorities", "Priorities"], ["appointments", "Appointments"]];
  return (
    <div className="p-6 space-y-4" data-testid="sales-ops-page">
      <PageHead title="Sales Operations" sub="Conversion engine — pipeline, priorities, appointments, follow-up automation" />
      <div className="flex gap-1.5">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" data-testid={`sales-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3 py-1.5 text-[9px] uppercase tracking-[0.16em] border ${tab === k ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C]/10"}`}>{l}</button>
        ))}
      </div>
      {tab === "pipeline" && <PipelineTab />}
      {tab === "priorities" && <PrioritiesTab />}
      {tab === "appointments" && <AppointmentsTab />}
    </div>
  );
}
